import { Button, Checkbox, Col, Modal, Row, Form, Input, Divider } from 'antd';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DATA_EXPORT_FORM_FIELDS } from 'util/config/constant';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { selectGlobalMyProfile } from 'state/global/profile/selector';
import {
  MEASUREMENT_FORMAT,
  MEASUREMENT_FORMAT_STORAGE_KEY
} from 'state/global/profile/util/constant';
import {
  CHECKBOX_PER_GRID_COLUMN,
  MAPPING_FILE_COLUMN_NAMES_BY_KEY
} from './util/constant';

export const CleaningReportExportModal = ({
  onFinish,
  renderFormContent,
  onClose,
  title,
  visible,
  loading,
  initialFormValues,
  width
}) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.CLEANING_REPORT);
  const [formInstance] = Form.useForm();
  const [, onFormFieldsChange] = useState({});
  const globalMyProfile = useSelector((state) => selectGlobalMyProfile(state));

  const mappingFileColumnNamesByKey = useMemo(() => {
    const {
      cleanedSquareMeter,
      cleanedSquareFeet,
      ...mappingWithoutSquareValues
    } = MAPPING_FILE_COLUMN_NAMES_BY_KEY;

    // prettier-ignore
    const validKeys = {
      ...mappingWithoutSquareValues,
      // eslint-disable-next-line max-len
      ...(globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY] === MEASUREMENT_FORMAT.METRIC ? { cleanedSquareMeter: 'exportCleaningReport.dialog.cleanedMeterSquare' } : {}),
      // eslint-disable-next-line max-len
      ...(globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY] === MEASUREMENT_FORMAT.IMPERIAL ? { cleanedSquareFeet: 'exportCleaningReport.dialog.cleanedFeetSquare' } : {}),
    };

    return validKeys;
  }, [globalMyProfile]);

  const checkedExportColumns = formInstance.getFieldValue(
    DATA_EXPORT_FORM_FIELDS.COLUMNS
  );

  const exportColumns = useMemo(() => {
    return Object.entries(mappingFileColumnNamesByKey).map(
      ([colKey, lngKey]) => ({
        name: t(lngKey),
        value: colKey
      })
    );
  }, [t, mappingFileColumnNamesByKey]);

  const indeterminate = useMemo(() => {
    return (
      checkedExportColumns &&
      !!checkedExportColumns.length &&
      checkedExportColumns.length < exportColumns.length
    );
  }, [checkedExportColumns, exportColumns.length]);

  const checkAll = useMemo(() => {
    return (
      checkedExportColumns &&
      checkedExportColumns.length === exportColumns.length
    );
  }, [checkedExportColumns, exportColumns]);

  const onCheckAll = useCallback(() => {
    const checkExportColumnValues = checkAll
      ? []
      : Object.keys(mappingFileColumnNamesByKey);

    formInstance.setFieldsValue({
      [DATA_EXPORT_FORM_FIELDS.COLUMNS]: checkExportColumnValues
    });
    onFormFieldsChange({});
  }, [checkAll, formInstance, mappingFileColumnNamesByKey]);

  const afterCloseModal = useCallback(() => {
    formInstance.resetFields();
  }, [formInstance]);

  useEffect(() => {
    // Set initial form values when opening the form modal
    if (visible) {
      formInstance.setFieldsValue(initialFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formInstance, visible]);

  const totalGridCols = useMemo(
    () => Math.ceil(exportColumns.length / CHECKBOX_PER_GRID_COLUMN),
    [exportColumns.length]
  );

  const renderCheckboxes = useCallback(() => {
    function generateCheckboxes(gridColumnIndex) {
      return Array.from({ length: CHECKBOX_PER_GRID_COLUMN }).map(
        (_, columnCheckboxIndex) => {
          const exportColumnIndex =
            columnCheckboxIndex + gridColumnIndex * CHECKBOX_PER_GRID_COLUMN;
          const exportColumnData = exportColumns[exportColumnIndex];

          if (!exportColumnData) {
            return null;
          }

          return (
            <Checkbox
              className="custom-checkbox file-column-checkbox"
              value={exportColumnData.value}>
              {exportColumnData.name}
            </Checkbox>
          );
        }
      );
    }

    const columns = Array.from({ length: totalGridCols }).map(
      (_, gridColumnIndex) => {
        return (
          <Col span={8} key={exportColumns[gridColumnIndex].value}>
            {generateCheckboxes(gridColumnIndex)}
          </Col>
        );
      }
    );

    return <Row>{columns}</Row>;
  }, [exportColumns, totalGridCols]);

  const handleFinishForm = (formValues) => {
    // Translate selected column names in an export file
    // ["machineSerialNumber", ...] => { "machineSerialNumber": "Serial Number", ... }
    const exportColumnValues = formValues[DATA_EXPORT_FORM_FIELDS.COLUMNS];

    if (exportColumnValues) {
      const mappingExportColumns = {};

      exportColumnValues.forEach((columnKey) => {
        mappingExportColumns[columnKey] = t(
          mappingFileColumnNamesByKey[columnKey]
        );
      });

      formValues[DATA_EXPORT_FORM_FIELDS.COLUMNS] = mappingExportColumns;
    }

    // Add translation for cleaning report row values
    formValues.rowValueTranslation = JSON.stringify({
      unit: t('unit', { returnObjects: true }),
      taskCompletionMapping: t('taskCompletionMapping', { returnObjects: true })
    });

    onFinish(formValues);
  };

  return (
    <Modal
      title={title}
      open={visible}
      className="base-modal no-select cleaning-report-export-modal"
      closable={false}
      onCancel={onClose}
      width={width}
      afterClose={afterCloseModal}
      footer={[
        <Row>
          <Col md={12} className="text-left">
            <Button className="cancel-btn" key="back" onClick={onClose}>
              {t('exportCleaningReport.dialog.cancelButton')}
            </Button>
          </Col>

          <Col md={12} className="base-yellow-btn-col">
            <Button
              loading={loading}
              key="submit"
              className="submit-btn"
              onClick={formInstance.submit}
              type="primary">
              {t('exportCleaningReport.dialog.submitButton')}
            </Button>
          </Col>
        </Row>
      ]}>
      <Form
        form={formInstance}
        onFinish={handleFinishForm}
        initialValues={initialFormValues}>
        {renderFormContent(formInstance)}

        <Divider className="custom-divider" orientation="left" plain>
          <span>{t('exportCleaningReport.dialog.hint')}</span>
          <Checkbox
            indeterminate={indeterminate}
            className="custom-checkbox file-column-checkbox"
            onChange={onCheckAll}
            skipGroup
            checked={checkAll}>
            <b>{t('exportCleaningReport.dialog.selectAll')}</b>
          </Checkbox>
        </Divider>

        <Form.Item hidden name={DATA_EXPORT_FORM_FIELDS.FILE_TYPE}>
          <Input />
        </Form.Item>

        <Form.Item name={DATA_EXPORT_FORM_FIELDS.COLUMNS}>
          <Checkbox.Group onChange={() => onFormFieldsChange({})}>
            {renderCheckboxes()}
          </Checkbox.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};

CleaningReportExportModal.defaultProps = {
  renderFormContent: () => null,
  loading: false,
  initialFormValues: {},
  width: 500
};

CleaningReportExportModal.propTypes = {
  renderFormContent: PropTypes.func,
  onFinish: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  initialFormValues: PropTypes.shape({
    [DATA_EXPORT_FORM_FIELDS.FILE_TYPE]: PropTypes.string,
    [DATA_EXPORT_FORM_FIELDS.COLUMNS]: PropTypes.arrayOf(PropTypes.string)
  }),
  width: PropTypes.number
};
